import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { login, resendVerification, getSubscriptionStatus } from '../services/api';

const Login: React.FC = () => {
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  useEffect(() => { const handler = () => setIsMobile(window.innerWidth <= 768); window.addEventListener('resize', handler); return () => window.removeEventListener('resize', handler); }, []);
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showResend, setShowResend] = useState(false);
  const [resendMessage, setResendMessage] = useState('');
  const [resending, setResending] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setShowResend(false);
    setResendMessage('');
    try {
      const response = await login(formData);
      const u = response.data.user;
      localStorage.setItem('token', response.data.token);
      localStorage.setItem('user', JSON.stringify(u));
      // Admin lands on the admin dashboard. Gate reads the real is_admin flag,
      // keeping id===110 as a fallback (matches the backend's
      // isAdminUser = is_admin || id===110, for the founding account's older tokens).
      if (u.is_admin === true || u.id === 110) {
        navigate('/admin');
      } else if (u.user_type === 'customer') {
        navigate('/my-requests');
      } else {
        // Business user. If they selected a paid tier but never completed checkout,
        // recover them to checkout instead of the requests feed. intendedPaidTier
        // is NOT on the login payload — it lives on /subscription/status — so fetch
        // it once here, only for business non-admins (no extra fetch on the
        // admin/customer paths). Token is already stored, so the call is authed.
        let intendedPaidTier: string | null = null;
        try {
          const statusRes = await getSubscriptionStatus();
          const sd: any = statusRes.data || {};
          intendedPaidTier = sd.intendedPaidTier ?? sd.subscription?.intendedPaidTier ?? null;
        } catch {
          // Non-fatal: if the status lookup fails, fall through to the normal feed.
        }
        if (intendedPaidTier === 'pro' || intendedPaidTier === 'premium') {
          navigate(`/pricing?tier=${intendedPaidTier}&checkout=1`);
        } else {
          navigate('/requests');
        }
      }
    } catch (err: any) {
      const errorMsg = err.response?.data?.error || 'Login failed';
      const errorCode = err.response?.data?.code;
      setError(errorMsg);
      if (errorCode === 'UNVERIFIED') {
        setShowResend(true);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setResending(true);
    setResendMessage('');
    try {
      const response = await resendVerification(formData.email);
      const data = response.data;
      if (data.code === 'MAX_RESENDS') {
        setResendMessage(data.error);
        setShowResend(false);
      } else if (data.remaining !== undefined) {
        const msg = data.remaining > 0
          ? `Verification email sent! Check your inbox. (${data.remaining} resend${data.remaining === 1 ? '' : 's'} remaining)`
          : `Verification email sent! This was your last resend.`;
        setResendMessage(msg);
        setShowResend(false);
      } else {
        setResendMessage(data.message || 'Verification email sent!');
        setShowResend(false);
      }
    } catch (err: any) {
      const errData = err.response?.data;
      if (errData?.code === 'MAX_RESENDS') {
        setResendMessage(errData.error);
        setShowResend(false);
      } else {
        setResendMessage('Failed to resend. Please try again.');
      }
    } finally {
      setResending(false);
    }
  };

  return (
    <div style={styles.page}>
      {/* Left Panel */}
      <div style={{...styles.leftPanel, display: isMobile ? 'none' : 'flex'}}>
        <div style={styles.leftContent}>
          <Link to="/" style={styles.backLink}>← Back to home</Link>
          <div style={styles.leftBadge}>Founding Member pricing — first 24 paid only</div>
          <h1 style={styles.leftTitle}>Welcome back to QuoteBid.</h1>
          <p style={styles.leftSubtitle}>
            The AI-powered marketplace connecting homeowners with local service businesses.
          </p>
          <div style={styles.statsList}>
            <div style={styles.stat}>
              <div style={styles.statNumber}>$0</div>
              <div style={styles.statLabel}>Lead Fees for Pros</div>
            </div>
            <div style={styles.stat}>
              <div style={styles.statNumber}>100%</div>
              <div style={styles.statLabel}>Free for Homeowners</div>
            </div>
            <div style={styles.stat}>
              <div style={styles.statNumber}>24</div>
              <div style={styles.statLabel}>Founding Member Spots</div>
            </div>
          </div>
          <div style={styles.newUserBox}>
            <p style={styles.newUserText}>Don't have an account yet?</p>
            <Link to="/register?type=business" style={styles.newUserBtn}>
              Create Business Account →
            </Link>
            <Link to="/register" style={styles.newUserBtnSecondary}>
              Sign up as Homeowner
            </Link>
          </div>
        </div>
      </div>

      {/* Right Panel */}
      <div style={styles.rightPanel}>
        <div style={styles.formContainer}>
          <div style={styles.formHeader}>
            <div style={styles.lockIcon}>🔐</div>
            <h2 style={styles.formTitle}>Sign in to your account</h2>
            <p style={styles.formSubtitle}>
              New here? <Link to="/register" style={styles.formLink}>Create an account</Link>
            </p>
          </div>

          {error && (
            <div style={styles.errorBox}>
              ⚠️ {error}
            </div>
          )}

          {showResend && (
            <div style={styles.resendBox}>
              <p style={styles.resendText}>Your email hasn't been verified yet. Need a new link?</p>
              <button onClick={handleResend} disabled={resending} style={styles.resendBtn}>
                {resending ? 'Sending...' : 'Resend Verification Email'}
              </button>
            </div>
          )}

          {resendMessage && (
            <div style={styles.successBox}>
              ✅ {resendMessage}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div style={styles.fieldGroup}>
              <label style={styles.label}>Email Address</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                style={styles.input}
                placeholder="you@example.com"
              />
            </div>

            <div style={styles.fieldGroup}>
              <div style={styles.passwordRow}>
                <label style={styles.label}>Password</label>
                <Link to="/forgot-password" style={styles.forgotLink}>Forgot password?</Link>
              </div>
              <input
                type="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                required
                style={styles.input}
                placeholder="Your password"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              style={{ ...styles.submitBtn, opacity: loading ? 0.7 : 1 }}
            >
              {loading ? 'Signing in...' : 'Sign In →'}
            </button>
          </form>

          <div style={styles.divider}>
            <span style={styles.dividerText}>New to QuoteBid?</span>
          </div>

          <div style={styles.registerOptions}>
            <Link to="/register?type=business" style={styles.registerBtn}>
              🏢 Join as a Business
            </Link>
            <Link to="/register" style={styles.registerBtnSecondary}>
              👤 Sign up as Homeowner
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  page: { display: 'flex', minHeight: '100vh', fontFamily: "'DM Sans', 'Segoe UI', system-ui, sans-serif" },
  leftPanel: { width: '420px', flexShrink: 0, background: 'linear-gradient(135deg, #0f172a 0%, #1e3a5f 50%, #0f4c75 100%)', padding: '48px 40px', flexDirection: 'column', justifyContent: 'flex-start' },
  leftContent: { maxWidth: '340px' },
  backLink: { color: 'rgba(255,255,255,0.5)', textDecoration: 'none', fontSize: '13px', marginBottom: '32px', display: 'block' },
  leftBadge: { display: 'inline-block', background: 'linear-gradient(135deg, rgba(251, 191, 36, 0.2), rgba(251, 191, 36, 0.1))', border: '1px solid rgba(251, 191, 36, 0.4)', color: '#fbbf24', padding: '6px 16px', borderRadius: '100px', fontSize: '12px', fontWeight: 600, marginBottom: '24px' },
  leftTitle: { fontSize: '32px', fontWeight: 800, color: '#ffffff', lineHeight: 1.2, marginBottom: '16px', letterSpacing: '-0.02em' },
  leftSubtitle: { fontSize: '15px', color: 'rgba(255,255,255,0.65)', lineHeight: 1.6, marginBottom: '40px' },
  statsList: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px', marginBottom: '40px', padding: '24px', background: 'rgba(255,255,255,0.07)', borderRadius: '12px', border: '1px solid rgba(255,255,255,0.1)' },
  stat: { textAlign: 'center' as const },
  statNumber: { fontSize: '28px', fontWeight: 800, background: 'linear-gradient(135deg, #3b82f6, #10b981)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text', marginBottom: '4px' },
  statLabel: { fontSize: '11px', color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase' as const, letterSpacing: '0.05em' },
  newUserBox: { display: 'flex', flexDirection: 'column' as const, gap: '10px' },
  newUserText: { fontSize: '13px', color: 'rgba(255,255,255,0.5)', margin: 0 },
  newUserBtn: { display: 'block', background: 'linear-gradient(135deg, #3b82f6, #10b981)', color: '#fff', padding: '12px 20px', borderRadius: '10px', textDecoration: 'none', fontSize: '14px', fontWeight: 700, textAlign: 'center' as const },
  newUserBtnSecondary: { display: 'block', background: 'rgba(255,255,255,0.07)', color: 'rgba(255,255,255,0.7)', padding: '12px 20px', borderRadius: '10px', textDecoration: 'none', fontSize: '14px', fontWeight: 500, textAlign: 'center' as const, border: '1px solid rgba(255,255,255,0.1)' },
  rightPanel: { flex: 1, background: '#f8fafc', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '48px 24px' },
  formContainer: { width: '100%', maxWidth: '440px' },
  formHeader: { textAlign: 'center' as const, marginBottom: '36px' },
  lockIcon: { fontSize: '40px', marginBottom: '16px' },
  formTitle: { fontSize: '28px', fontWeight: 800, color: '#0f172a', letterSpacing: '-0.02em', marginBottom: '8px' },
  formSubtitle: { fontSize: '14px', color: '#64748b' },
  formLink: { color: '#3b82f6', textDecoration: 'none', fontWeight: 600 },
  errorBox: { background: '#fef2f2', border: '1px solid #fecaca', color: '#dc2626', padding: '12px 16px', borderRadius: '10px', fontSize: '14px', marginBottom: '20px' },
  resendBox: { background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: '10px', padding: '16px', marginBottom: '20px', textAlign: 'center' as const },
  resendText: { margin: '0 0 12px 0', color: '#92400e', fontSize: '14px' },
  resendBtn: { background: 'linear-gradient(135deg, #3b82f6, #10b981)', color: '#fff', border: 'none', borderRadius: '8px', padding: '10px 24px', fontSize: '14px', fontWeight: 600, cursor: 'pointer' },
  successBox: { background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '10px', padding: '12px 16px', marginBottom: '20px', color: '#065f46', fontSize: '14px', textAlign: 'center' as const },
  fieldGroup: { marginBottom: '20px' },
  passwordRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' },
  label: { display: 'block', fontSize: '13px', fontWeight: 600, color: '#374151' },
  forgotLink: { fontSize: '13px', color: '#3b82f6', textDecoration: 'none', fontWeight: 500 },
  input: { width: '100%', padding: '14px 16px', fontSize: '15px', border: '1px solid #e2e8f0', borderRadius: '10px', background: '#ffffff', color: '#0f172a', outline: 'none', boxSizing: 'border-box' as const },
  submitBtn: { width: '100%', padding: '15px 24px', background: 'linear-gradient(135deg, #3b82f6, #10b981)', color: '#ffffff', border: 'none', borderRadius: '12px', fontSize: '16px', fontWeight: 700, cursor: 'pointer', boxShadow: '0 4px 16px rgba(59, 130, 246, 0.3)', marginBottom: '28px' },
  divider: { display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' },
  dividerText: { flex: 1, textAlign: 'center' as const, fontSize: '12px', fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase' as const, letterSpacing: '0.1em', borderTop: '1px solid #e2e8f0', paddingTop: '20px' },
  registerOptions: { display: 'flex', flexDirection: 'column' as const, gap: '10px' },
  registerBtn: { display: 'block', background: '#0f172a', color: '#fff', padding: '14px 24px', borderRadius: '10px', textDecoration: 'none', fontSize: '15px', fontWeight: 700, textAlign: 'center' as const },
  registerBtnSecondary: { display: 'block', background: '#ffffff', color: '#0f172a', padding: '14px 24px', borderRadius: '10px', textDecoration: 'none', fontSize: '15px', fontWeight: 600, textAlign: 'center' as const, border: '1px solid #e2e8f0' },
};

export default Login;
